import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UPSELL_PRODUCTS, DEFAULT_UPSELL_SLUG } from '../config/offers';
import styles from './UpsellFirewall.module.css';
import MatrixCanvas from '../components/HomeComponents/MatrixCanvas';
import logoRed from '../assets/logo.png';
import { addUtmsToUrl } from '../utils/utm';

const FAQ_ITEMS = [
  {
    question: 'Preciso fazer outro pagamento?',
    answer: 'Essa taxa é separada da compra inicial. Ela serve exclusivamente para ativar os servidores criptografados, garantir o modo invisível e liberar os backups automáticos com sigilo total.'
  },
  {
    question: 'Aparece o nome do app na cobrança?',
    answer: 'Não. A cobrança aparece com um nome discreto e genérico para proteger sua privacidade.'
  },
  {
    question: 'Essa taxa é realmente obrigatória?',
    answer: 'Sim. Sem ela, o sistema não pode ativar os protocolos de segurança e privacidade, o que compromete todo o funcionamento.'
  },
  {
    question: 'Posso usar o app sem ativar o firewall?',
    answer: 'Não. A ativação do firewall é necessária para liberar o acesso completo e garantir o sigilo das operações.'
  }
];

const BENEFITS = [
  'Proteção total dos seus dados e identidade',
  '100% de sigilo na investigação',
  'Liberação de backups automáticos',
  'Firewall ativo por 1 ano após o pagamento'
];

const UpsellFirewall = () => {
  const [searchParams] = useSearchParams();
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [progressWidth, setProgressWidth] = useState(0);

  const productSlug = searchParams.get('product') || DEFAULT_UPSELL_SLUG;
  const product = UPSELL_PRODUCTS[productSlug] || UPSELL_PRODUCTS[DEFAULT_UPSELL_SLUG];

  // Barra de progresso sobe ao abrir a página
  useEffect(() => {
    if (!product) return;
    const t = setTimeout(() => setProgressWidth(60), 200);
    return () => clearTimeout(t);
  }, [product]);

  function handleCheckout() {
    if (!product?.checkoutUrl) return;
    window.location.href = addUtmsToUrl(product.checkoutUrl);
  }

  function toggleFaq(index) {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  }

  const priceDisplay = product.priceDisplay || 'R$ 47,00';
  const originalPrice = product.originalPrice || null;

  // Separa parte principal e centavos (o que vem depois da vírgula fica menor, como no Cta)
  const commaIdx = priceDisplay.indexOf(',');
  const priceMain = commaIdx !== -1 ? priceDisplay.slice(0, commaIdx) : priceDisplay;
  const priceCents = commaIdx !== -1 ? priceDisplay.slice(commaIdx) : '';

  return (
    <div className={styles.upsellPage}>
      <MatrixCanvas />

      {/* 1. Banner fixo */}
      <div className={styles.banner}>
        <p className={styles.bannerTitle}>Atenção, não saia dessa página!</p>
        <p className={styles.bannerSubtitle}>Sua compra ainda não foi 100% concluída</p>
      </div>

      {/* 2. Header: logo + barra de progresso (animada ao abrir) */}
      <header className={styles.header}>
        <img src={logoRed} alt="Red Stalker" className={styles.logo} />
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressWidth}%` }}
            role="progressbar"
            aria-valuenow={progressWidth}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </header>

      {/* 3. Main */}
      <main className={styles.main}>
        <div className={styles.container}>
          {/* Card 1: Parabéns (h1) + título médio + subtítulo + alerta */}
          <div className={styles.cardParabens}>
            <div className={styles.cardParabensHead}>
              <h1 className={styles.cardParabensH1}>
                <span className={styles.highlight}>Parabéns!</span>{' '}
                <span className={styles.emojiCelebration}>🎉</span>
              </h1>
              <p className={styles.tituloMedio}>
                Seu acesso ao <span className={styles.highlight}>PinkStalker</span>
                <br />
                está quase concluído!
              </p>
            </div>
            <p className={styles.subtitle}>
              Para garantir total sigilo da sua identidade, é necessário realizar um pagamento único de uma pequena taxa de Firewall & Sigilo.
            </p>
            <div className={styles.alertWarning}>
              <span className={styles.alertIcon}>⚠️</span>
              <span><strong>Ativação obrigatória:</strong> sem essa taxa, o Instagram pode notificar seu cônjuge que está sendo monitorado e expor sua identidade por medidas de segurança.</span>
            </div>
          </div>

          {/* Linha CTA - texto maior, highlight com gradient */}
          <p className={styles.ctaLine}>
            Ative o <span className={styles.highlight}>Firewall</span> e acesse o <span className={styles.highlight}>Instagram</span> agora mesmo!
          </p>

          {/* Card 2: Oferta Especial */}
          <div className={styles.cardOferta}>
            <div className={styles.badgeLimitado}>Limitado</div>
            <h2 className={styles.cardOfertaTitle}>
              <span className={styles.cardOfertaTitleText}>Oferta Especial</span>{' '}
              <span className={styles.fireIcon} aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path className={styles.fireFlame1} d="M12 2C10 5 8 7 8 11c0 3 2 5 4 5s4-2 4-5c0-4-2-6-4-9z" fill="url(#fireGrad1)" />
                  <path className={styles.fireFlame2} d="M12 7c-2 2-3 4-3 6 0 2.5 1.5 4 3 4s3-1.5 3-4c0-2-1-4-3-6z" fill="url(#fireGrad2)" />
                  <defs>
                    <linearGradient id="fireGrad1" x1="12" y1="2" x2="12" y2="16" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#ff6b35" />
                      <stop offset="0.4" stopColor="#f7931e" />
                      <stop offset="0.7" stopColor="#ff4500" />
                      <stop offset="1" stopColor="#FF0D6B" />
                    </linearGradient>
                    <linearGradient id="fireGrad2" x1="12" y1="7" x2="12" y2="17" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#ffd700" />
                      <stop offset="0.5" stopColor="#ff8c00" />
                      <stop offset="1" stopColor="#ff4500" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>
            <ul className={styles.pillsList}>
              {BENEFITS.map((text, i) => (
                <li key={i} className={styles.pill}>
                  <svg className={styles.checkIcon} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <div className={styles.priceBlock}>
              {originalPrice && (
                <div className={styles.pricingOld}>De: {originalPrice}</div>
              )}
              <div className={styles.pricingPrice}>
                <span className={styles.pricingCurrency}>R$</span>
                <span className={styles.pricingValue}>{priceMain.replace('R$', '').trim()}</span>
                {priceCents && <span className={styles.pricingCents}>{priceCents}</span>}
              </div>
              <div className={styles.priceBadges}>
                <span className={styles.paymentTerm}>Pagamento único</span>
              </div>
            </div>
            <button type="button" className={styles.ctaButton} onClick={handleCheckout}>
              <span>Proteger meus dados e sigilo</span>
              <span>E acessar o Instagram agora mesmo</span>
            </button>
          </div>

          {/* FAQ */}
          <section className={styles.faq}>
            <h3 className={styles.faqTitle}>Perguntas Frequentes</h3>
            {FAQ_ITEMS.map((item, index) => (
              <div key={index} className={styles.faqItem}>
                <button
                  type="button"
                  className={styles.faqQuestion}
                  onClick={() => toggleFaq(index)}
                  aria-expanded={activeFaqIndex === index}
                >
                  <span>{item.question}</span>
                  <span className={styles.faqPlus}>{activeFaqIndex === index ? '−' : '+'}</span>
                </button>
                <div
                  className={styles.faqAnswerWrapper}
                  style={{ maxHeight: activeFaqIndex === index ? 400 : 0 }}
                  aria-hidden={activeFaqIndex !== index}
                >
                  <div className={styles.faqAnswer}>{item.answer}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Footer */}
          <footer className={styles.footer}>
            <img src={logoRed} alt="Red Stalker" className={styles.footerLogo} />
            <p className={styles.footerCopyright}>© 2025 PinkStalker — Todos os direitos reservados.®</p>
            <div className={styles.footerLinks}>
              <a href="#termos">Termos de Uso</a>
              <a href="#privacidade">Política de Privacidade</a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default UpsellFirewall;
